import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { OwnerOnlyBanner } from "./DashboardLayout";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Calendar } from "@/components/ui/calendar";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  Calendar as CalendarIcon, Clock, MapPin, Plus, Pencil, Trash2, Loader2,
  ChevronLeft, ChevronRight, UserX, AlertCircle, CheckCircle2, Pin, PinOff, Lock,
} from "lucide-react";
import { toast } from "sonner";
import { PosterStripes, PosterGrain, PosterRibbon, PosterSection } from "@/components/PosterFX";

const isoToday = () => new Date().toISOString().slice(0, 10);
const monthLabel = (d) => d.toLocaleDateString(undefined, { month: "long", year: "numeric" });
const formatEventDate = (iso) => new Date(iso + "T00:00:00").toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });

const EMPTY_EVENT = { id: null, title: "", description: "", date: isoToday(), time: "", location: "", pinned: false };

export default function Itinerary() {
  const { user } = useAuth();
  const isOwner = user?.role === "owner";

  if (!isOwner) {
    return (
      <div className="flex flex-col items-center justify-center py-24 text-center" data-testid="itinerary-owner-gate">
        <Lock className="w-10 h-10 text-[#71717A] mb-4" />
        <h2 className="font-display text-xl text-white">OWNER ONLY</h2>
        <p className="text-sm text-[#A1A1AA] mt-2 max-w-sm">The full itinerary is managed by the owner. Pinned events appear for everyone on the Headquarters page.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Tabs defaultValue="daily" className="w-full">
        <TabsList className="bg-[#121212] border border-[#27272A] rounded-sm p-1 flex flex-wrap h-auto max-w-full justify-start">
          <TabsTrigger value="daily" data-testid="itinerary-tab-daily" className="data-[state=active]:bg-[#C58AF9]/15 data-[state=active]:text-[#C58AF9] data-[state=active]:border-[#C58AF9]/40 border border-transparent font-label text-xs px-4">
            Daily Schedule
          </TabsTrigger>
          <TabsTrigger value="calendar" data-testid="itinerary-tab-calendar" className="data-[state=active]:bg-[#C58AF9]/15 data-[state=active]:text-[#C58AF9] data-[state=active]:border-[#C58AF9]/40 border border-transparent font-label text-xs px-4">
            Calendar
          </TabsTrigger>
          {isOwner && (
            <TabsTrigger value="attendance" data-testid="itinerary-tab-attendance" className="data-[state=active]:bg-[#C58AF9]/15 data-[state=active]:text-[#C58AF9] data-[state=active]:border-[#C58AF9]/40 border border-transparent font-label text-xs px-4">
              Attendance
            </TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="daily" className="mt-6"><DailySchedule isOwner={isOwner} /></TabsContent>
        <TabsContent value="calendar" className="mt-6"><CalendarView isOwner={isOwner} /></TabsContent>
        {isOwner && <TabsContent value="attendance" className="mt-6"><AttendanceTab /></TabsContent>}
      </Tabs>
    </div>
  );
}


/* ------------------------ DAILY SCHEDULE ------------------------ */
function DailySchedule({ isOwner }) {
  const [selectedDate, setSelectedDate] = useState(isoToday());
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ ...EMPTY_EVENT, date: selectedDate });
  const [saving, setSaving] = useState(false);

  const load = async (date) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/schedule/events?start=${date}&end=${date}`);
      setEvents(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(selectedDate); }, [selectedDate]);

  const shift = (days) => {
    const d = new Date(selectedDate + "T00:00:00");
    d.setDate(d.getDate() + days);
    setSelectedDate(d.toISOString().slice(0, 10));
  };

  const openCreate = () => { setForm({ ...EMPTY_EVENT, date: selectedDate }); setDialogOpen(true); };
  const openEdit = (ev) => { setForm({ id: ev.id, title: ev.title, description: ev.description || "", date: ev.date, time: ev.time || "", location: ev.location || "", pinned: !!ev.pinned }); setDialogOpen(true); };

  const save = async () => {
    if (!form.title.trim()) return toast.error("Title is required.");
    const payload = { title: form.title.trim(), description: form.description.trim(), date: form.date, time: form.time || null, location: form.location.trim(), pinned: !!form.pinned };
    setSaving(true);
    try {
      if (form.id) await api.patch(`/schedule/events/${form.id}`, payload);
      else await api.post("/schedule/events", payload);
      toast.success(form.id ? "Event updated" : "Event added");
      setDialogOpen(false);
      await load(selectedDate);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setSaving(false); }
  };

  const togglePin = async (ev) => {
    try {
      await api.patch(`/schedule/events/${ev.id}`, { pinned: !ev.pinned });
      toast.success(ev.pinned ? "Unpinned from Overview" : "Pinned to Overview");
      await load(selectedDate);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (ev) => {
    if (!window.confirm(`Delete "${ev.title}"?`)) return;
    try {
      await api.delete(`/schedule/events/${ev.id}`);
      toast.success("Event removed");
      await load(selectedDate);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div data-testid="daily-schedule-panel" className="space-y-6">
      {/* Date bar */}
      <div
        className="relative border-2 overflow-hidden flex flex-wrap items-center justify-between gap-3 p-3"
        style={{
          backgroundColor: "#0A0A0A",
          borderColor: "#C58AF988",
          backgroundImage: "linear-gradient(160deg, #C58AF918, transparent 60%)",
          boxShadow: "0 0 18px #C58AF922, inset 0 0 18px rgba(0,0,0,0.5)",
        }}
      >
        <PosterStripes color="#C58AF9" size={72} opacity={0.5} />
        <PosterGrain opacity={0.04} />
        <div className="relative flex items-center gap-2">
          <button onClick={() => shift(-1)} className="p-2 hover:bg-[#0A0A0A] rounded-sm text-[#A1A1AA]" data-testid="day-prev-button" aria-label="Previous day"><ChevronLeft className="w-4 h-4" /></button>
          <div className="font-display text-lg md:text-xl min-w-0 sm:min-w-[240px] text-center" data-testid="daily-selected-date">
            {formatEventDate(selectedDate)}
          </div>
          <button onClick={() => shift(1)} className="p-2 hover:bg-[#0A0A0A] rounded-sm text-[#A1A1AA]" data-testid="day-next-button" aria-label="Next day"><ChevronRight className="w-4 h-4" /></button>
          <Button onClick={() => setSelectedDate(isoToday())} className="ml-2 bg-[#27272A] hover:bg-[#3F3F46] text-white font-label text-xs h-8 px-3 rounded-sm" data-testid="jump-today-button">Today</Button>
        </div>
        {isOwner && (
          <Button onClick={openCreate} className="relative bg-[#C58AF9] hover:bg-[#B77CE5] text-black font-label h-9 px-4 rounded-sm" data-testid="add-event-button">
            <Plus className="w-4 h-4 mr-2" /> Add event
          </Button>
        )}
      </div>

      {/* Events list */}
      <PosterSection color="#C58AF9" ribbon="★ TODAY'S RUN OF SHOW ★" testid="daily-events-panel">
        {loading ? (
          <div className="p-8 flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin text-[#A1A1AA]" /></div>
        ) : events.length === 0 ? (
          <div className="p-12 text-center" data-testid="daily-empty-state">
            <CalendarIcon className="w-12 h-12 text-[#C58AF9]/30 mx-auto mb-3" />
            <div className="font-display text-lg mb-1">NOTHING SCHEDULED</div>
            <p className="text-sm text-[#A1A1AA]">
              {isOwner ? "Add a team meeting, target push, or training block." : "Your owner hasn't scheduled anything for this day."}
            </p>
          </div>
        ) : (
          <ul className="divide-y divide-[#C58AF9]/15">
            {events.map((ev) => (
              <li key={ev.id} className="p-4 flex items-start gap-4 hover:bg-[#0A0A0A]/50" data-testid={`event-row-${ev.id}`}>
                <div className="w-16 flex-shrink-0 text-center">
                  {ev.time ? (
                    <>
                      <div className="font-mono text-lg text-[#C58AF9]">{ev.time}</div>
                      <div className="text-[10px] font-label text-[#A1A1AA] tracking-wider">
                        {parseInt(ev.time.split(":")[0], 10) >= 12 ? "PM" : "AM"}
                      </div>
                    </>
                  ) : (
                    <div className="text-xs font-label text-[#A1A1AA] tracking-wider">ALL DAY</div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium flex items-center gap-2">
                    {ev.pinned && (
                      <span className="text-[10px] font-label px-1.5 py-0.5 bg-[#FFC832]/10 text-[#FFC832] border border-[#FFC832]/40 rounded-sm flex items-center gap-1" data-testid={`event-pinned-badge-${ev.id}`}>
                        <Pin className="w-2.5 h-2.5" /> PINNED
                      </span>
                    )}
                    <span className="truncate">{ev.title}</span>
                  </div>
                  {ev.description && <div className="text-sm text-[#A1A1AA] mt-1 whitespace-pre-wrap">{ev.description}</div>}
                  {ev.location && <div className="text-xs text-[#A1A1AA] mt-1 flex items-center gap-1"><MapPin className="w-3 h-3" /> {ev.location}</div>}
                </div>
                {isOwner && (
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => togglePin(ev)}
                      className={`p-2 hover:bg-[#121212] rounded-sm ${ev.pinned ? "text-[#FFC832]" : "text-[#A1A1AA] hover:text-[#FFC832]"}`}
                      title={ev.pinned ? "Unpin from Overview" : "Pin to Overview"}
                      data-testid={`event-pin-${ev.id}`}
                    >
                      {ev.pinned ? <PinOff className="w-4 h-4" /> : <Pin className="w-4 h-4" />}
                    </button>
                    <button onClick={() => openEdit(ev)} className="p-2 hover:bg-[#121212] rounded-sm text-[#A1A1AA] hover:text-white" data-testid={`event-edit-${ev.id}`}><Pencil className="w-4 h-4" /></button>
                    <button onClick={() => remove(ev)} className="p-2 hover:bg-[#E13C32]/10 rounded-sm text-[#A1A1AA] hover:text-[#E13C32]" data-testid={`event-delete-${ev.id}`}><Trash2 className="w-4 h-4" /></button>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </PosterSection>

      {isOwner && <EventDialog open={dialogOpen} onOpenChange={setDialogOpen} form={form} setForm={setForm} onSave={save} saving={saving} />}
    </div>
  );
}


/* ------------------------ CALENDAR VIEW ------------------------ */
function CalendarView({ isOwner }) {
  const [month, setMonth] = useState(new Date());
  const [events, setEvents] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_EVENT);
  const [saving, setSaving] = useState(false);

  const monthStart = useMemo(() => {
    const d = new Date(month.getFullYear(), month.getMonth(), 1);
    return d.toISOString().slice(0, 10);
  }, [month]);
  const monthEnd = useMemo(() => {
    const d = new Date(month.getFullYear(), month.getMonth() + 1, 0);
    return d.toISOString().slice(0, 10);
  }, [month]);

  const load = async () => {
    try {
      const { data } = await api.get(`/schedule/events?start=${monthStart}&end=${monthEnd}`);
      setEvents(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  useEffect(() => { load(); }, [monthStart, monthEnd]);

  const eventsByDate = useMemo(() => {
    const map = {};
    for (const e of events) (map[e.date] = map[e.date] || []).push(e);
    return map;
  }, [events]);

  const selectedIso = selectedDate ? selectedDate.toISOString().slice(0, 10) : null;
  const eventsForSelected = selectedIso ? (eventsByDate[selectedIso] || []) : [];
  const eventDates = useMemo(() => Object.keys(eventsByDate).map((d) => new Date(d + "T00:00:00")), [eventsByDate]);

  const openCreateForSelected = () => {
    setForm({ ...EMPTY_EVENT, date: selectedIso });
    setDialogOpen(true);
  };

  const save = async () => {
    if (!form.title.trim()) return toast.error("Title is required.");
    const payload = { title: form.title.trim(), description: form.description.trim(), date: form.date, time: form.time || null, location: form.location.trim(), pinned: !!form.pinned };
    setSaving(true);
    try {
      if (form.id) await api.patch(`/schedule/events/${form.id}`, payload);
      else await api.post("/schedule/events", payload);
      toast.success(form.id ? "Event updated" : "Event added");
      setDialogOpen(false);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const togglePin = async (ev) => {
    try {
      await api.patch(`/schedule/events/${ev.id}`, { pinned: !ev.pinned });
      toast.success(ev.pinned ? "Unpinned from Overview" : "Pinned to Overview");
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (ev) => {
    if (!window.confirm(`Delete "${ev.title}"?`)) return;
    try {
      await api.delete(`/schedule/events/${ev.id}`);
      toast.success("Event removed");
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="calendar-panel">
      <PosterSection color="#C58AF9" ribbon="★ MONTH AT A GLANCE ★">
        <div className="p-4 pt-0">
        <div className="flex items-center justify-between mb-2">
          <div className="font-display text-lg" data-testid="calendar-month-label">{monthLabel(month)}</div>
        </div>
        <Calendar
          mode="single"
          selected={selectedDate}
          onSelect={(d) => d && setSelectedDate(d)}
          month={month}
          onMonthChange={setMonth}
          modifiers={{ hasEvent: eventDates }}
          modifiersClassNames={{ hasEvent: "font-bold underline decoration-[#C58AF9] decoration-2 underline-offset-2" }}
          className="rounded-md"
          data-testid="itinerary-calendar-grid"
        />
        </div>
      </PosterSection>

      <PosterSection color="#C58AF9" ribbon="★ SELECTED DAY ★">
        <div className="p-4 pt-0">
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="font-display text-xl" data-testid="calendar-selected-date">
              {selectedDate ? formatEventDate(selectedIso) : "Pick a date"}
            </div>
            <div className="text-xs text-[#A1A1AA] mt-1">{eventsForSelected.length} event{eventsForSelected.length === 1 ? "" : "s"}</div>
          </div>
          {isOwner && selectedDate && (
            <Button onClick={openCreateForSelected} className="bg-[#C58AF9] hover:bg-[#B77CE5] text-black font-label h-9 px-4 rounded-sm" data-testid="calendar-add-event-button">
              <Plus className="w-4 h-4 mr-2" /> Add
            </Button>
          )}
        </div>

        {eventsForSelected.length === 0 ? (
          <div className="py-8 text-center text-sm text-[#A1A1AA]" data-testid="calendar-day-empty">No events on this day.</div>
        ) : (
          <ul className="space-y-3">
            {eventsForSelected.map((ev) => (
              <li key={ev.id} className="p-3 bg-[#0A0A0A] border border-[#C58AF9]/20 rounded-sm">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 text-xs text-[#C58AF9] font-mono mb-1">
                      <Clock className="w-3 h-3" /> {ev.time || "All day"}
                      {ev.pinned && (
                        <span className="text-[10px] font-label px-1.5 py-0.5 bg-[#FFC832]/10 text-[#FFC832] border border-[#FFC832]/40 rounded-sm flex items-center gap-1" data-testid={`event-pinned-badge-${ev.id}`}>
                          <Pin className="w-2.5 h-2.5" /> PINNED
                        </span>
                      )}
                    </div>
                    <div className="font-medium">{ev.title}</div>
                    {ev.description && <div className="text-sm text-[#A1A1AA] mt-1 whitespace-pre-wrap">{ev.description}</div>}
                    {ev.location && <div className="text-xs text-[#A1A1AA] mt-1 flex items-center gap-1"><MapPin className="w-3 h-3" /> {ev.location}</div>}
                  </div>
                  {isOwner && (
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <button onClick={() => togglePin(ev)} className={`p-1.5 hover:bg-[#121212] rounded-sm ${ev.pinned ? "text-[#FFC832]" : "text-[#A1A1AA] hover:text-[#FFC832]"}`} title={ev.pinned ? "Unpin from Overview" : "Pin to Overview"} data-testid={`event-pin-${ev.id}`}>
                        {ev.pinned ? <PinOff className="w-3.5 h-3.5" /> : <Pin className="w-3.5 h-3.5" />}
                      </button>
                      <button onClick={() => { setForm({ id: ev.id, title: ev.title, description: ev.description || "", date: ev.date, time: ev.time || "", location: ev.location || "", pinned: !!ev.pinned }); setDialogOpen(true); }} className="p-1.5 hover:bg-[#121212] rounded-sm text-[#A1A1AA] hover:text-white"><Pencil className="w-3.5 h-3.5" /></button>
                      <button onClick={() => remove(ev)} className="p-1.5 hover:bg-[#E13C32]/10 rounded-sm text-[#A1A1AA] hover:text-[#E13C32]"><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
        </div>
      </PosterSection>

      {isOwner && <EventDialog open={dialogOpen} onOpenChange={setDialogOpen} form={form} setForm={setForm} onSave={save} saving={saving} />}
    </div>
  );
}


/* ------------------------ EVENT DIALOG (shared) ------------------------ */
function EventDialog({ open, onOpenChange, form, setForm, onSave, saving }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="text-white max-w-lg p-0 border-2"
        style={{
          backgroundColor: "#0A0A0A",
          borderColor: "#C58AF9bb",
          backgroundImage: "linear-gradient(160deg, #C58AF922, transparent 55%)",
          boxShadow: "0 0 30px #C58AF955, inset 0 0 24px rgba(0,0,0,0.55)",
        }}
        data-testid="event-dialog"
      >
        <PosterStripes color="#C58AF9" size={96} opacity={0.85} />
        <PosterGrain opacity={0.06} />
        <PosterRibbon color="#C58AF9" size="sm">★ {form.id ? "RESCHEDULE" : "BOOK THE DATE"} ★</PosterRibbon>
        <div className="relative p-4 pt-6 sm:p-5 sm:pt-7">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">{form.id ? "EDIT EVENT" : "NEW EVENT"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div>
            <Label className="font-label text-xs">Title</Label>
            <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. Weekly kickoff, Team lunch" className="bg-[#121212] border-[#27272A] mt-1" data-testid="event-title-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="font-label text-xs">Date</Label>
              <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="bg-[#121212] border-[#27272A] mt-1" data-testid="event-date-input" />
            </div>
            <div>
              <Label className="font-label text-xs">Time (optional)</Label>
              <Input type="time" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} placeholder="Leave blank for all-day" className="bg-[#121212] border-[#27272A] mt-1" data-testid="event-time-input" />
            </div>
          </div>
          <div>
            <Label className="font-label text-xs">Location (optional)</Label>
            <Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="e.g. Conference Room B, Zoom" className="bg-[#121212] border-[#27272A] mt-1" data-testid="event-location-input" />
          </div>
          <div>
            <Label className="font-label text-xs">Description (optional)</Label>
            <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Details reps should know" className="bg-[#121212] border-[#27272A] mt-1 min-h-[80px]" data-testid="event-desc-input" />
          </div>
          <label className="flex items-start gap-3 p-3 bg-[#121212] border border-[#27272A] hover:border-[#FFC832]/40 rounded-sm cursor-pointer transition-colors" data-testid="event-pin-toggle">
            <input
              type="checkbox"
              checked={!!form.pinned}
              onChange={(e) => setForm({ ...form, pinned: e.target.checked })}
              className="mt-0.5 h-4 w-4 accent-[#FFC832] cursor-pointer"
              data-testid="event-pin-checkbox"
            />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 text-sm">
                <Pin className="w-3.5 h-3.5 text-[#FFC832]" />
                <span className="font-medium">Pin to Overview page</span>
              </div>
              <div className="text-[11px] text-[#A1A1AA] mt-0.5">
                Pinned events appear above every rep&apos;s stat tiles on the Overview page until the event date passes.
              </div>
            </div>
          </label>
        </div>
        <DialogFooter>
          <Button onClick={() => onOpenChange(false)} className="bg-[#27272A] hover:bg-[#3F3F46] text-white font-label rounded-sm">Cancel</Button>
          <Button onClick={onSave} disabled={saving} className="bg-[#E13C32] hover:bg-[#EB5A50] text-white font-label rounded-sm" data-testid="event-save-button">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : form.id ? "Save changes" : "Add event"}
          </Button>
        </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
}


/* ------------------------ ATTENDANCE TAB (OWNER ONLY) ------------------------ */
function AttendanceTab() {
  const [employees, setEmployees] = useState([]);
  const [records, setRecords] = useState([]);
  const [lateCounts, setLateCounts] = useState({}); // {user_id: N late in past 30d}
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ user_id: "", date: isoToday(), status: "late", time: "", notes: "" });
  const [saving, setSaving] = useState(false);
  const [filterUserId, setFilterUserId] = useState("all");
  const [loading, setLoading] = useState(false);

  const loadEmployees = async () => {
    try {
      const { data } = await api.get("/employees");
      setEmployees(data.filter((e) => e.role !== "owner"));
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const loadLateCounts = async () => {
    // Rolling 30-day late tally across ALL reps (unfiltered by user)
    try {
      const d = new Date();
      d.setDate(d.getDate() - 30);
      const start = d.toISOString().slice(0, 10);
      const { data } = await api.get(`/attendance?start=${start}`);
      const counts = {};
      for (const r of data) {
        if (r.status === "late") counts[r.user_id] = (counts[r.user_id] || 0) + 1;
      }
      setLateCounts(counts);
    } catch (e) { /* non-blocking */ }
  };

  const loadRecords = async () => {
    setLoading(true);
    try {
      const qs = filterUserId && filterUserId !== "all" ? `?user_id=${filterUserId}` : "";
      const { data } = await api.get(`/attendance${qs}`);
      setRecords(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadEmployees(); loadLateCounts(); }, []);
  useEffect(() => { loadRecords(); }, [filterUserId]);

  const save = async () => {
    if (!form.user_id) return toast.error("Pick an employee.");
    setSaving(true);
    try {
      const body = { ...form };
      if (!body.time) body.time = null; // don't send an empty string
      await api.post("/attendance", body);
      toast.success(`Marked ${form.status} for ${form.date}${form.time ? ` @ ${form.time}` : ""}`);
      setDialogOpen(false);
      setForm({ user_id: "", date: isoToday(), status: "late", time: "", notes: "" });
      await Promise.all([loadRecords(), loadLateCounts()]);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const remove = async (r) => {
    if (!window.confirm(`Remove ${r.status} record for ${r.user_name} on ${r.date}?`)) return;
    try {
      await api.delete(`/attendance/${r.id}`);
      toast.success("Record removed");
      await Promise.all([loadRecords(), loadLateCounts()]);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  // Group records by month for readability
  const grouped = useMemo(() => {
    const g = {};
    for (const r of records) {
      const m = r.date.slice(0, 7); // YYYY-MM
      (g[m] = g[m] || []).push(r);
    }
    return g;
  }, [records]);

  return (
    <div className="space-y-4" data-testid="attendance-panel">
      <OwnerOnlyBanner note="Only you see this Attendance tab — employees never know when they've been marked late or absent unless you tell them." testid="attendance-owner-only-banner" />

      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div className="flex items-center gap-3">
          <Label className="font-label text-xs whitespace-nowrap">FILTER BY REP:</Label>
          <Select value={filterUserId} onValueChange={setFilterUserId}>
            <SelectTrigger className="bg-[#121212] border-[#27272A] h-9 min-w-[220px] rounded-sm" data-testid="attendance-filter-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#121212] border-[#27272A] text-white">
              <SelectItem value="all">All employees</SelectItem>
              {employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <Button onClick={() => setDialogOpen(true)} className="bg-[#C58AF9] hover:bg-[#B77CE5] text-black font-label h-9 px-4 rounded-sm" data-testid="add-attendance-button">
          <Plus className="w-4 h-4 mr-2" /> Log absence / lateness
        </Button>
      </div>

      {loading ? (
        <div className="p-8 flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin text-[#A1A1AA]" /></div>
      ) : records.length === 0 ? (
        <PosterSection color="#C58AF9" ribbon="★ CLEAN RECORD ★">
          <div className="p-8 text-center" data-testid="attendance-empty">
            <CheckCircle2 className="w-12 h-12 text-[#34C759]/60 mx-auto mb-3" />
            <div className="font-display text-lg mb-1">CLEAN RECORD</div>
            <p className="text-sm text-[#A1A1AA]">No lateness or absences logged {filterUserId !== "all" ? "for this rep" : ""}. Keep it going.</p>
          </div>
        </PosterSection>
      ) : (
        <div className="space-y-4">
          {Object.keys(grouped).sort().reverse().map((month) => (
            <PosterSection key={month} color="#C58AF9" ribbon={`★ ${new Date(month + "-01T00:00:00").toLocaleDateString(undefined, { month: "long", year: "numeric" }).toUpperCase()} ★`}>
              <ul className="divide-y divide-[#C58AF9]/15">
                {grouped[month].map((r) => (
                  <li key={r.id} className="p-4 flex items-center gap-4" data-testid={`attendance-row-${r.id}`}>
                    <div className={`w-10 h-10 rounded-sm flex items-center justify-center flex-shrink-0 border ${r.status === "absent" ? "bg-[#E13C32]/10 border-[#E13C32]/30 text-[#E13C32]" : "bg-[#EAB132]/10 border-[#EAB132]/30 text-[#EAB132]"}`}>
                      {r.status === "absent" ? <UserX className="w-5 h-5" /> : <AlertCircle className="w-5 h-5" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium flex items-center gap-2 flex-wrap">
                        <span className="truncate">{r.user_name}</span>
                        {(lateCounts[r.user_id] || 0) > 0 && (
                          <span
                            className="text-xs font-label font-semibold tracking-wide px-2 py-0.5 rounded-sm bg-[#EAB132] text-black"
                            title={`Late ${lateCounts[r.user_id]} time(s) in the past 30 days`}
                            data-testid={`late-30d-count-${r.user_id}`}
                          >
                            {lateCounts[r.user_id]}× late · 30d
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-[#A1A1AA]">
                        <span className={`font-label uppercase tracking-wider ${r.status === "absent" ? "text-[#E13C32]" : "text-[#EAB132]"}`}>{r.status}</span>
                        {" · "}{formatEventDate(r.date)}
                        {r.time && <> · <span className="font-mono text-[#C58AF9]">{r.time}</span></>}
                      </div>
                      {r.notes && <div className="text-xs text-[#A1A1AA] mt-1 italic">&ldquo;{r.notes}&rdquo;</div>}
                    </div>
                    <button onClick={() => remove(r)} className="p-2 hover:bg-[#E13C32]/10 rounded-sm text-[#A1A1AA] hover:text-[#E13C32]" data-testid={`attendance-delete-${r.id}`} title="Remove record"><Trash2 className="w-4 h-4" /></button>
                  </li>
                ))}
              </ul>
            </PosterSection>
          ))}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent
          className="text-white max-w-lg p-0 border-2"
          style={{
            backgroundColor: "#0A0A0A",
            borderColor: "#C58AF9bb",
            backgroundImage: "linear-gradient(160deg, #C58AF922, transparent 55%)",
            boxShadow: "0 0 30px #C58AF955, inset 0 0 24px rgba(0,0,0,0.55)",
          }}
          data-testid="attendance-dialog"
        >
          <PosterStripes color="#C58AF9" size={96} opacity={0.85} />
          <PosterGrain opacity={0.06} />
          <PosterRibbon color="#C58AF9" size="sm">★ ATTENDANCE REPORT ★</PosterRibbon>
          <div className="relative p-4 pt-6 sm:p-5 sm:pt-7">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">LOG ATTENDANCE</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="font-label text-xs">Employee</Label>
              <Select value={form.user_id} onValueChange={(v) => setForm({ ...form, user_id: v })}>
                <SelectTrigger className="bg-[#121212] border-[#27272A] mt-1 rounded-sm" data-testid="attendance-user-select">
                  <SelectValue placeholder="Pick a rep..." />
                </SelectTrigger>
                <SelectContent className="bg-[#121212] border-[#27272A] text-white">
                  {employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="font-label text-xs">Date</Label>
                <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="bg-[#121212] border-[#27272A] mt-1" data-testid="attendance-date-input" />
              </div>
              <div>
                <Label className="font-label text-xs">Status</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger className="bg-[#121212] border-[#27272A] mt-1 rounded-sm" data-testid="attendance-status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#121212] border-[#27272A] text-white">
                    <SelectItem value="late">Late</SelectItem>
                    <SelectItem value="absent">Absent</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="font-label text-xs">Time (optional)</Label>
              <Input
                type="time"
                value={form.time}
                onChange={(e) => setForm({ ...form, time: e.target.value })}
                className="bg-[#121212] border-[#27272A] mt-1"
                data-testid="attendance-time-input"
              />
              <p className="text-[10px] text-[#A1A1AA] mt-1">
                {form.status === "absent"
                  ? "e.g. when they were expected on the floor."
                  : "e.g. what time they actually clocked in."}
              </p>
            </div>
            <div>
              <Label className="font-label text-xs">Notes (optional)</Label>
              <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="e.g. 20 min late, no notice given" className="bg-[#121212] border-[#27272A] mt-1 min-h-[70px]" data-testid="attendance-notes-input" />
            </div>
          </div>
          <DialogFooter>
            <Button onClick={() => setDialogOpen(false)} className="bg-[#27272A] hover:bg-[#3F3F46] text-white font-label rounded-sm">Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-[#C58AF9] hover:bg-[#B77CE5] text-black font-label rounded-sm" data-testid="attendance-save-button">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Log record"}
            </Button>
          </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
